import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { getRequest } from "services/api";

const AttendanceGraphChart = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    // Start date: 25th of previous month
    const startDate = new Date(currentYear, currentMonth - 1, 26);
    // End date: 25th of current month
    const endDate = new Date(currentYear, currentMonth, 26);

    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
    };
  });

  useEffect(() => {
    const fetchAttendanceGraph = async () => {
      try {
        setLoading(true);
        const queryString = `?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
        const response = await getRequest(`/ahr/statistics/attendance-graph${queryString}`);
        if (response.success) {
          setAttendanceData(response.data);
        }
      } catch (error) {
        console.error("Error fetching attendance graph:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceGraph();
  }, [dateRange]);

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDateForAxis = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    // Show date only for 1st, 15th, and last day of month, otherwise show empty string
    if (day === 1 || day === 15 || day === new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return '';
  };

  const chartOptions = {
    chart: {
      type: "area",
      height: 300,
      toolbar: {
        show: false,
      },
      fontFamily: "Inter, sans-serif",
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    xaxis: {
      categories: attendanceData.map(item => formatDateForAxis(item.date)),
      labels: {
        style: {
          fontFamily: "Inter, sans-serif",
          fontSize: "12px",
        },
        rotate: 0,
        hideOverlappingLabels: true,
        trim: true,
      },
      axisBorder: {
        show: true,
      },
      axisTicks: {
        show: true,
      },
    },
    yaxis: {
      labels: {
        style: {
          fontFamily: "Inter, sans-serif",
          fontSize: "12px",
        },
      },
      min: 0,
    },
    colors: ["#2fb344", "#d63939"], // Tabler success and danger colors
    legend: {
      position: "top",
      fontFamily: "Inter, sans-serif",
      fontSize: "14px",
      markers: {
        width: 12,
        height: 12,
        radius: 2,
      },
    },
    tooltip: {
      style: {
        fontFamily: "Inter, sans-serif",
        fontSize: "12px",
      },
      x: {
        formatter: function(value, { dataPointIndex }) {
          if (attendanceData[dataPointIndex]) {
            const date = new Date(attendanceData[dataPointIndex].date);
            return date.toLocaleDateString('en-US', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });
          }
          return value;
        }
      },
      y: {
        formatter: function(value) {
          return value + " employees";
        }
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        opacityFrom: 0.4,
        opacityTo: 0.1,
      },
    },
    grid: {
      show: true,
      borderColor: '#f1f3f4',
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false,
        }
      },
      yaxis: {
        lines: {
          show: true,
        }
      },
    },
  };

  const chartSeries = [
    {
      name: "Working",
      data: attendanceData.map(item => item.working),
    },
    {
      name: "Not Working",
      data: attendanceData.map(item => item.not_working),
    },
  ];

  if (loading) {
    return (
      <div className="col-lg-12 mb-4">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Attendance Graph</h3>
          </div>
          <div className="card-body">
            <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-lg-12">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Attendance Graph</h3>
          <div className="card-actions">
            <div className="input-group input-group-sm" style={{ width: "auto" }}>
              <input
                type="date"
                className="form-control"
                value={dateRange.start_date}
                onChange={(e) => handleDateChange('start_date', e.target.value)}
              />
              <span className="input-group-text">to</span>
              <input
                type="date"
                className="form-control"
                value={dateRange.end_date}
                onChange={(e) => handleDateChange('end_date', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="card-body">
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="area"
            height={300}
          />
        </div>
      </div>
    </div>
  );
};

export default AttendanceGraphChart;
